/**
 * Technical Analysis panel (v1.4.0, Part 4): renders the explained output of
 * the Technical Analysis Engine — the Technical Score and signal, the trend,
 * every indicator's plain-English read grouped by category, and where the
 * indicators agree or disagree. Loads live from the company's price bars.
 */

import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/api/client";
import type { TechnicalAnalysis, TechnicalIndicatorReading } from "@/types";

const CATEGORY_ORDER = ["trend", "momentum", "volatility", "volume", "levels"];
const CATEGORY_LABELS: Record<string, string> = {
  trend: "Trend",
  momentum: "Momentum",
  volatility: "Volatility",
  volume: "Volume",
  levels: "Levels",
};

function readingSignalClass(signal: string): string {
  if (signal === "bullish") return "text-signal-teal";
  if (signal === "bearish") return "text-signal-rose";
  return "text-mist-400";
}

function overallSignalClass(signal: string): string {
  if (signal.includes("Buy")) return "text-signal-teal";
  if (signal.includes("Sell")) return "text-signal-rose";
  return "text-mist-300";
}

export default function TechnicalAnalysisPanel({ companyId }: { companyId: string }): React.ReactElement | null {
  const [data, setData] = useState<TechnicalAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    apiClient
      .get<TechnicalAnalysis>(`/companies/${companyId}/technical-analysis`)
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load technical analysis.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const grouped = useMemo(() => {
    const map = new Map<string, TechnicalIndicatorReading[]>();
    for (const reading of data?.readings ?? []) {
      const list = map.get(reading.category) ?? [];
      list.push(reading);
      map.set(reading.category, list);
    }
    return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => [c, map.get(c) as TechnicalIndicatorReading[]] as const);
  }, [data]);

  if (error) return null;

  if (isLoading || !data) {
    return (
      <div className="panel p-6">
        <h2 className="text-lg font-semibold text-mist-100">Technical analysis</h2>
        <p className="mt-3 text-sm text-mist-400">Loading technical analysis…</p>
      </div>
    );
  }

  const thinData = data.bars_used < 20;

  return (
    <div className="panel p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-mist-100">Technical analysis</h2>
          <p className="mt-1 text-xs text-mist-400">
            {data.bars_used} bars · trend {data.trend} · {data.bullish_count} bullish / {data.bearish_count} bearish /{" "}
            {data.neutral_count} neutral signals
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-3xl font-semibold text-mist-100">
            {data.technical_score.toFixed(0)}
            <span className="text-base text-mist-400">/100</span>
          </p>
          <p className={`text-sm font-semibold ${overallSignalClass(data.signal)}`}>{data.signal}</p>
          <p className="text-xs text-mist-400">confidence {(data.confidence * 100).toFixed(0)}%</p>
        </div>
      </div>

      {thinData ? (
        <p className="mt-4 text-sm text-mist-400">
          {data.risks[0] ?? "Not enough price history for a full technical read."}
        </p>
      ) : (
        <>
          <div className="mt-5 space-y-4">
            {grouped.map(([category, readings]) => (
              <div key={category}>
                <h3 className="text-xs font-medium uppercase tracking-wide text-mist-400">
                  {CATEGORY_LABELS[category] ?? category}
                </h3>
                <ul className="mt-1.5 space-y-1.5">
                  {readings.map((reading) => (
                    <li key={reading.key} className="flex items-baseline gap-2 text-sm">
                      <span className={`w-16 shrink-0 text-xs uppercase ${readingSignalClass(reading.signal)}`}>
                        {reading.signal}
                      </span>
                      <span className="text-mist-200">{reading.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {(data.agreements.length > 0 || data.disagreements.length > 0) && (
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {data.agreements.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-signal-teal">Agreement</h3>
                  <ul className="mt-1 space-y-1 text-sm text-mist-300">
                    {data.agreements.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.disagreements.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-signal-amber">Disagreement</h3>
                  <ul className="mt-1 space-y-1 text-sm text-mist-300">
                    {data.disagreements.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {data.risks.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-medium uppercase tracking-wide text-signal-amber">Risks to watch</h3>
              <ul className="mt-1 space-y-1 text-sm text-mist-300">
                {data.risks.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-4 border-t border-ink-800 pt-3 text-sm text-mist-300">{data.suggested_action}</p>
        </>
      )}
    </div>
  );
}
